import dayjs from 'dayjs';
import { PhoneNumberType, PhoneNumberUtil } from 'google-libphonenumber';

export class Utils {
  public static FormatCurrency(value: number, round = false): string {
    const _formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    });
    value /= 100;
    const formatted = _formatter.format(value);
    return round ? formatted.split(/\./)[0] : formatted;
  }

  public static FormatDate(value: string | Date, format = 'D/M/YYYY'): string {
    if (!value) return '-';
    return dayjs(value).format(format);
  }

  public static FormatDateTime(value: string | Date, format = 'D/M/YYYY HH:mm'): string {
    if (!value) return '-';
    return dayjs(value).format(format);
  }

  public static FormatTime(value: string | Date, format = 'DD MMM HH:mm'): string {
    if (!value) return '-';
    return dayjs(value).format(format);
  }

  public static ValidateEmail(email: string): boolean {
    return /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      String(email).toLowerCase(),
    );
  }

  public static ValidateMobile(number: string): boolean {
    return number.replace(/[\s-]*/g, '').match(/^04\d{8}$/) !== null;
  }

  public static Json2Csv(data: (string | number | null | undefined)[][], headers: (string | null)[]): string {
    return [headers.filter((v) => v !== null).join(','), data.map((line) => line.filter((v) => v !== null).map((v) => `"${v}"`)).join('\n')].join('\n');
  }

  public static UUID() {
    return Math.round(Math.random() * 100000);
  }

  public static DownloadFile(data: string | Buffer, filename = 'export.csv', type = 'application/json') {
    const element = document.createElement('a');
    const file = new Blob([data as BlobPart], { type });
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
  }

  public static FormatPhone(number: string): string {
    const matches = number.match(/^(\d{4})(\d{3})(\d{3})$/);
    // console.log(matches);
    if (!matches) return number;
    return matches.slice(1, 4).join(' ');
  }

  public static Post(path: string, params: any, method = 'post') {
    const form = document.createElement('form');
    form.method = method;
    form.action = path;

    for (const key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = key;
        hiddenField.value = params[key];

        form.appendChild(hiddenField);
      }
    }

    document.body.appendChild(form);
    form.submit();
  }

  public static Truncate(s: string, length = 30): string {
    if (!s) return '';
    return s.length > length ? s.slice(0, length) + '...' : s;
  }

  public static Get(obj: any, key: string): any {
    try {
      const value = key
        .split('.')
        .map((k) => {
          if (obj[k] === undefined || obj[k] === null) throw 'not found';
          obj = obj[k];
          return obj;
        })
        .pop();
      return value;
    } catch (e) {
      return undefined;
    }
  }

  public static GenerateUUIDv4(): string {
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
      (Number(c) ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (Number(c) / 4)))).toString(16),
    );
  }

  public static Hash(str: string): string {
    let hash: number = 0,
      i,
      chr;
    if (str.length === 0) return hash.toString();
    for (i = 0; i < str.length; i++) {
      chr = str.charCodeAt(i);
      hash = (hash << 5) - hash + chr;
      hash |= 0; // Convert to 32bit integer
    }
    return hash.toString();
  }

  public static SplitWord(str: string): string {
    return str ? str.split(/(?=[A-Z])/).join(' ') : '';
  }

  public static isPhoneValid = (phone: string) => {
    const phoneUtil = PhoneNumberUtil.getInstance();

    try {
      return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
    } catch (error) {
      return false;
    }
  };

  public static isMobilePhoneValid = (phone: string) => {
    const phoneUtil = PhoneNumberUtil.getInstance();

    try {
      const parsed = phoneUtil.parseAndKeepRawInput(phone);
      if (!phoneUtil.isValidNumber(parsed)) {
        return false;
      }
      const numberType = phoneUtil.getNumberType(parsed);
      return numberType === PhoneNumberType.MOBILE || numberType === PhoneNumberType.FIXED_LINE_OR_MOBILE;
    } catch {
      return false;
    }
  };

  public static normalizePhoneNumber(number: string): string | null {
    // Remove all non-digit characters
    let normalizedNumber = number.replace(/\D/g, '');

    // If the number starts with '04', add the country code
    if (normalizedNumber.startsWith('04')) {
      normalizedNumber = `61${normalizedNumber.slice(1)}`;
    }

    // If the number starts with '+61', remove the '+'
    if (normalizedNumber.startsWith('+')) {
      normalizedNumber = normalizedNumber.slice(1);
    }

    // Remove leading '0' after '61' if present
    if (normalizedNumber.startsWith('610')) {
      normalizedNumber = `61${normalizedNumber.slice(3)}`;
    }

    return `${normalizedNumber}`;
  }
}
